<template>
  <div class="split-account-container">
    <!-- 收款方列表 -->
    <div v-for="(recipient, index) in recipients" :key="index" class="recipient-section">
      <!-- 删除按钮 -->
      <div v-if="recipients.length > 1" class="delete-btn-wrapper">
        <el-button
          type="danger"
          text
          size="small"
          @click="removeRecipient(index)"
          :disabled="isDisabled"
          class="delete-btn"
        >
          删除
        </el-button>
      </div>

      <div class="recipient-header">收款方{{ index + 1 }}</div>

      <!-- 分账比例 -->
      <div class="form-group">
        <label class="form-label">
          <span style="color: red">*</span>
          分账比例
        </label>
        <div class="input-wrapper">
          <input
            v-model.number="recipient.splitRatio"
            type="number"
            class="ratio-input"
            :min="1"
            :max="30"
            :disabled="isDisabled"
            placeholder="请输入分账比例"
            @blur="handleRatioInput(index, $event)"
          />
          <span class="suffix">%</span>
        </div>
        <div class="hint-text">最高30%，最低1%</div>
      </div>

      <!-- 收款类型 -->
      <div class="form-group">
        <label class="form-label">
          <span style="color: red">*</span>
          收款类型
        </label>
        <div class="radio-group">
          <label class="radio-label">
            <input
              v-model="recipient.splitType"
              type="radio"
              value="MERCHANT"
              :disabled="isDisabled"
              class="radio-input"
              @change="handleTypeChange(index, 'MERCHANT')"
            />
            <span class="radio-custom"></span>
            <span class="radio-text">手机号直分</span>
          </label>
          <label class="radio-label">
            <input
              v-model="recipient.splitType"
              type="radio"
              value="ONLY_NUM"
              :disabled="isDisabled"
              class="radio-input"
              @change="handleTypeChange(index, 'ONLY_NUM')"
            />
            <span class="radio-custom"></span>
            <span class="radio-text">仅作记录</span>
          </label>
        </div>
      </div>

      <!-- 手机号/OpenID搜索（仅在手机号直分时显示） -->
      <div v-if="recipient.splitType === 'MERCHANT'" class="form-group">
        <label class="form-label">
          搜索用户
          <span class="required">*</span>
        </label>
        <div class="search-wrapper">
          <div class="search-input-wrapper" :class="{ 'has-selection': recipient.receiverAccount }">
            <input
              v-model="recipient.receiverAccount"
              type="text"
              class="search-input"
              :disabled="isDisabled"
              @blur="updateParentModel"
              placeholder="请输入手机号或用户openid搜索"
            />
          </div>
        </div>
        <div class="hint-text">如未搜索到用户，先让用户登录兔魅力小程序后再试</div>
      </div>

      <!-- 收款方姓名/名称 -->
      <div class="form-group">
        <label class="form-label">
          {{ recipient.splitType === 'MERCHANT' ? '收款方姓名' : '收款方名称' }}
          <span class="required">*</span>
        </label>
        <input
          v-model="recipient.receiverName"
          type="text"
          class="name-input"
          :disabled="isDisabled"
          @blur="updateParentModel"
          :placeholder="recipient.splitType === 'MERCHANT' ? '请输入收款方姓名' : '请输入收款方名称'"
        />
      </div>

      <!-- 提示文字（仅在仅作记录时显示） -->
      <div v-if="recipient.splitType === 'ONLY_NUM'" class="hint-text form-hint">
        该选项不做分账，仅作订单分账明细记录，便于业务线下打款
      </div>
    </div>

    <!-- 增加收款方按钮 -->
    <el-button class="add-recipient-btn" @click="addRecipient" :disabled="recipients.length >= 10">
      + 增加收款方
    </el-button>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, reactive, nextTick, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'

// 用户类型定义
interface User {
  id: string
  name: string
  phone: string
  openId: string
}

// 收款方类型定义
interface Recipient {
  receiverOrder: number
  splitRatio: number | null
  splitType: 'MERCHANT' | 'ONLY_NUM' | ''
  receiverName: string
  searchKeyword?: string
  receiverAccount?: string | User
  receiverDesc?: string
}

const props = withDefaults(
  defineProps<{
    model: Record<string, any>
    prop: string
    hidden?: boolean
    disabled?: boolean
    labelProp: string
  }>(),
  {
    hidden: false,
    disabled: false
  }
)

// 使用计算属性获取禁用状态，避免在模板中直接使用 props.disabled
const isDisabled = computed(() => props.model.isView)

// 收款方列表
const recipients = ref<Recipient[]>([
  {
    receiverOrder: 1,
    splitRatio: null,
    splitType: '',
    receiverName: ''
  }
])

// 监听外部数据变化，初始化收款方列表
watch(
  () => props.model[props.prop],
  newValue => {
    console.log('payment-collection 监听到数据变化:', newValue)
    if (newValue && Array.isArray(newValue) && newValue.length > 0) {
      recipients.value = newValue.map((item: any, index: number) => ({
        ...item,
        receiverOrder: index + 1, // 重新计算序号
        splitRatio: item.splitRatio || null,
        splitType: item.splitType || '',
        receiverName: item.receiverName || '',
        receiverAccount: item.receiverAccount || '',
        receiverDesc: item.receiverDesc || ''
      }))
      console.log('payment-collection 初始化后的 recipients:', recipients.value)
    }
  },
  { immediate: true, deep: true }
)

// 组件挂载时初始化数据
onMounted(() => {
  console.log('payment-collection 组件挂载，props.model[props.prop]:', props.model[props.prop])
  if (props.model[props.prop] && Array.isArray(props.model[props.prop]) && props.model[props.prop].length > 0) {
    recipients.value = props.model[props.prop].map((item: any, index: number) => ({
      ...item,
      receiverOrder: index + 1, // 重新计算序号
      splitRatio: item.splitRatio || null,
      splitType: item.splitType || '',
      receiverName: item.receiverName || '',
      receiverAccount: item.receiverAccount || '',
      receiverDesc: item.receiverDesc || ''
    }))
    console.log('payment-collection onMounted 后的 recipients:', recipients.value)
  }

  // 阻止所有 number 类型输入框的滚轮事件
  document.addEventListener(
    'wheel',
    e => {
      if (e.target.type === 'number') {
        e.preventDefault()
      }
    },
    { passive: false }
  )
})

// 搜索相关状态
const showSearchResults = reactive<boolean[]>([])
const searchResults = reactive<User[][]>([])
const isSearching = reactive<boolean[]>([])

// 模拟用户数据
const mockUsers: User[] = [
  { id: '1', name: '张三', phone: '13800138000', openId: 'openid_001' },
  { id: '2', name: '李四', phone: '13800138001', openId: 'openid_002' },
  { id: '3', name: '王五', phone: '13800138002', openId: 'openid_003' },
  { id: '4', name: '赵六', phone: '13800138003', openId: 'openid_004' }
]

// 总比例计算
const totalRatio = computed(() => {
  return recipients.value.reduce((sum, recipient) => {
    return sum + (recipient.splitRatio || 0)
  }, 0)
})

// 处理分账比例输入
const handleRatioInput = (index: number, event: Event) => {
  const input = event.target as HTMLInputElement
  let value = parseInt(input.value) || 0

  if (value < 1) {
    value = 1
  } else if (value > 30) {
    value = 30
  }

  recipients.value[index].splitRatio = value

  if (totalRatio.value > 100) {
    const exceed = totalRatio.value - 100
    recipients.value[index].splitRatio = Math.max(1, (recipients.value[index].splitRatio || 0) - exceed)
  }

  updateParentModel()
}

// 处理收款类型变化
const handleTypeChange = (index: number, type: 'MERCHANT' | 'ONLY_NUM') => {
  recipients.value[index].splitType = type
  updateParentModel()
}

// 添加收款方
const addRecipient = () => {
  if (recipients.value.length >= 10) {
    ElMessage.warning('最多只能添加10个收款方')
    return
  }

  const lastIndex = recipients.value.length - 1
  if (lastIndex < 0) {
    addNewRecipient()
    return
  }

  const lastRecipient = recipients.value[lastIndex]
  const validationError = validateRecipient(lastRecipient, lastIndex + 1)

  if (validationError) {
    ElMessage.warning(validationError)
    return
  }

  if (totalRatio.value > 30) {
    ElMessage.warning(`当前总分账比例为${totalRatio.value}%，已超过30%。请调整后再添加。`)
    return
  }

  addNewRecipient()
}

// 删除收款方
const removeRecipient = async (index: number) => {
  if (recipients.value.length <= 1) {
    ElMessage.warning('至少需要保留一个收款方')
    return
  }

  try {
    await ElMessageBox.confirm('确定要删除这个收款方吗？', '确认删除', {
      type: 'warning',
      confirmButtonText: '确定',
      cancelButtonText: '取消'
    })

    // 删除对应的搜索相关状态
    showSearchResults.splice(index, 1)
    searchResults.splice(index, 1)
    isSearching.splice(index, 1)

    // 删除收款方
    recipients.value.splice(index, 1)

    // 重新计算序号
    recipients.value.forEach((recipient, i) => {
      recipient.receiverOrder = i + 1
    })

    // 同步到父组件
    updateParentModel()

    ElMessage.success('删除成功')
  } catch (error) {
    // 用户取消了删除操作
    console.log('用户取消了删除操作')
  }
}

// 验证单个收款方
const validateRecipient = (recipient: Recipient, index: number): string | null => {
  if (!recipient.splitRatio || recipient.splitRatio < 1 || recipient.splitRatio > 30) {
    return `收款方${index}的分账比例需在1%-30%之间`
  }

  if (!recipient.splitType) {
    return `请选择收款方${index}的收款类型`
  }

  if (!recipient.receiverName?.trim()) {
    return `收款方${index}的${recipient.splitType === 'MERCHANT' ? '姓名' : '名称'}不能为空`
  }

  if (recipient.splitType === 'MERCHANT' && !recipient.receiverAccount) {
    return `收款方${index}需要输入手机号或用户openid`
  }

  return null
}

// 添加新收款方的辅助函数
const addNewRecipient = () => {
  recipients.value.push({
    receiverOrder: recipients.value.length + 1,
    splitRatio: null,
    splitType: '',
    receiverName: ''
  })

  showSearchResults.push(false)
  searchResults.push([])
  isSearching.push(false)
  updateParentModel()
}

// 更新父组件模型的方法
const updateParentModel = () => {
  // 重新计算序号，确保一致性
  recipients.value.forEach((recipient, index) => {
    recipient.receiverOrder = index + 1
  })

  // 使用 nextTick 确保 DOM 更新完成后再更新父组件
  nextTick(() => {
    // 深拷贝数据，避免引用问题
    const recipientsCopy = JSON.parse(JSON.stringify(recipients.value))
    props.model[props.labelProp] = recipientsCopy
    console.log('更新父组件数据:', recipientsCopy)
  })
}
</script>

<style scoped lang="scss">
.split-account-container {
  width: 100%;
  box-sizing: border-box;
  max-width: 600px;
  margin: 0 auto;
  padding: 24px;
  background: #fff;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  position: relative;
}

.recipient-section {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  background: #fafafa;
  position: relative;

  &:last-of-type {
    margin-bottom: 0;
  }
}

.recipient-header {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
}

.delete-btn-wrapper {
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 1;
}

.delete-btn {
  padding: 4px 8px;
  font-size: 12px;
  border-radius: 4px;

  &:hover {
    background-color: rgba(255, 77, 79, 0.1);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
}

.form-group {
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
}

.form-label {
  display: block;
  font-size: 14px;
  color: #333;
  margin-bottom: 8px;
  font-weight: 500;
  .required {
    color: #ff4d4f;
    margin-left: 4px;
  }
}

.input-wrapper {
  display: flex;
  align-items: center;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: #fff;
  transition: all 0.3s;
  overflow: hidden;
  width: 200px;

  &:focus-within {
    border-color: #1890ff;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }
}

.ratio-input {
  flex: 1;
  height: 36px;
  padding: 0 12px;
  border: none;
  outline: none;
  font-size: 14px;
  background: transparent;

  &::placeholder {
    color: #bfbfbf;
  }

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  -moz-appearance: textfield;
}

.suffix {
  padding: 0 12px;
  font-size: 14px;
  color: #666;
  background: #fafafa;
  height: 36px;
  display: flex;
  align-items: center;
  border-left: 1px solid #d9d9d9;
}

// 搜索框样式
.search-wrapper {
  position: relative;
  width: 300px;
}

.search-input-wrapper {
  display: flex;
  align-items: center;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: #fff;
  transition: all 0.3s;
  overflow: hidden;

  &.has-selection {
    background: #f0f8ff;
    border-color: #91d5ff;
  }

  &:focus-within {
    border-color: #1890ff;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }
}

.search-input {
  flex: 1;
  height: 36px;
  padding: 0 12px;
  border: none;
  outline: none;
  font-size: 14px;
  background: transparent;

  &::placeholder {
    color: #bfbfbf;
  }
}

.name-input {
  width: 300px;
  height: 36px;
  padding: 0 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;
  transition: all 0.3s;
  background: #fff;

  &:focus {
    outline: none;
    border-color: #1890ff;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }

  &::placeholder {
    color: #bfbfbf;
  }
}

.hint-text {
  font-size: 12px;
  color: #999;
  margin-top: 6px;
  line-height: 1.5;
}

.form-hint {
  margin-top: 8px;
  padding: 8px 12px;
  background: #f0f0f0;
  border-radius: 4px;
  border-left: 3px solid #999;
}

.radio-group {
  display: flex;
  gap: 24px;
}

.radio-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
}

.radio-input {
  display: none;

  &:checked + .radio-custom {
    border-color: #1890ff;

    &::after {
      transform: scale(1);
    }
  }
}

.radio-custom {
  width: 16px;
  height: 16px;
  border: 1px solid #d9d9d9;
  border-radius: 50%;
  margin-right: 8px;
  position: relative;
  transition: all 0.3s;

  &::after {
    content: '';
    position: absolute;
    top: 3px;
    left: 3px;
    width: 8px;
    height: 8px;
    background: #1890ff;
    border-radius: 50%;
    transform: scale(0);
    transition: transform 0.3s;
  }
}

.radio-text {
  font-size: 14px;
  color: #333;
}

.add-recipient-btn {
  width: 100%;
  height: 40px;
  background: #fff;
  border: 1px dashed #d9d9d9;
  border-radius: 4px;
  color: #1890ff;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s;
  margin-top: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover:not(:disabled) {
    border-color: #1890ff;
    background: #f0f8ff;
  }

  &:disabled {
    color: #bfbfbf;
    cursor: not-allowed;
    border-color: #f0f0f0;
  }
}
</style>
